import { Suspense } from 'react';
import { RouteFallback } from '@/app/RouteFallback';
import { useHeldPermissions } from '@/app/usePermission';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';
import { useSession } from '@/app/session';
import { ADMIN_NAV, NAV, navFilter } from '@/app/shells/cabinet/cabinetNav';
import { CabinetHeader } from '@/app/shells/cabinet/CabinetHeader';
import { CabinetSidebar } from '@/app/shells/cabinet/CabinetSidebar';
import { useCabinetDocumentTitle } from '@/app/shells/cabinet/useCabinetDocumentTitle';
import { useCabinetDrawer } from '@/app/shells/cabinet/useCabinetDrawer';
import { PAGE_VIEW, usePageViewCounter } from '@/features/common/usePageViewCounter';
import { appShell } from '@/i18n/dictionaries/appShell';
import { useTranslation } from '@/i18n/useTranslation';
import { GovBar } from '@/features/common/layout/GovBar';
import { SITE_PAGE } from '@/features/common/layout/publicPageLinks';
import '@/app/shells/shell.css';

export function AppShell() {
  const { user } = useSession();
  // setLanguage moved out with the gov-bar: GovBar owns the RO/EN switch for both zones now.
  const { t } = useTranslation(appShell);
  const location = useLocation();
  const roles = new Set(user?.roles ?? []);
  const permissions = useHeldPermissions();
  const items = navFilter(NAV, roles, permissions);
  const adminItems = navFilter(ADMIN_NAV, roles, permissions);
  const allItems = [...items, ...adminItems];

  useCabinetDocumentTitle(allItems);
  // The cabinet counts its own pages since T236: the servlet filter stopped counting /app/** and
  // the requests behind it, because an XHR is not a page and one dossier is not a page apart from
  // the next. FlowShell reports the pages that are not under this shell.
  usePageViewCounter(PAGE_VIEW.cabinet);
  const drawer = useCabinetDrawer(location.pathname);

  return (
    <div className="app-shell">
      {/* react-router's data router doesn't reset scroll on navigation by default —
          without this, switching sidebar items keeps whatever scroll offset the
          previous, differently-sized page had, reading as the new page "jumping". */}
      <ScrollRestoration />
      <a className="skip-link" href="#main">
        {t('skipLink')}
      </a>
      {/* Shared with the public zone (features/common/layout/GovBar). The cabinet passes no `contained`
          because it spans the full viewport, and offers only Accesibilitate — its way back to the
          public site is the AKIS logo in the header below, not a gov-bar link.
          Verified before the swap: every string GovBar prints is byte-identical, in both
          languages, to the appShell keys this block used to read. */}
      <GovBar links={<a href={SITE_PAGE.accessibility}>{t('govBarAccessibility')}</a>} />
      <CabinetHeader
        drawerOpen={drawer.drawerOpen}
        toggleRef={drawer.drawerToggleRef}
        onToggle={drawer.toggle}
      />
      <div className="app-layout">
        <CabinetSidebar
          items={items}
          adminItems={adminItems}
          drawerOpen={drawer.drawerOpen}
          drawerRef={drawer.drawerRef}
          onOverlayClick={drawer.close}
          onCloseClick={drawer.closeAndRefocus}
        />
        <main className="app-main" id="main">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {/* Mirrors the public site's .footer-bottom row (layout/footer.html) — same three
          policy links + MCloud-hosting copyright line, so the cabinet's footer reads as
          the same chrome as the public site's, not a different design. */}
      <footer className="app-footer">
        <div className="app-footer__links">
          <a href="/politici/utilizare">{t('footerTerms')}</a>
          <a href="/politici/confidentialitate">{t('footerPrivacy')}</a>
          <a href="/politici/cookies">{t('footerCookies')}</a>
          <a href={SITE_PAGE.accessibility}>{t('footerAccessibility')}</a>
        </div>
        <span className="app-footer__copyright">{t('footerCopyright')}</span>
      </footer>
    </div>
  );
}
